import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Download, ArrowLeft, RefreshCw, Trash2, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-yellow-100 text-yellow-800",
  converted: "bg-green-100 text-green-800",
  lost: "bg-gray-100 text-gray-600",
};

function getAuthHeaders() {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/leads/count`, { headers }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, []);

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/leads/${id}`, { status }, { headers: getAuthHeaders() });
    fetchData();
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    await axios.delete(`${API}/leads/${id}`, { headers: getAuthHeaders() });
    fetchData();
  };

  const exportCSV = async () => {
    const token = localStorage.getItem("admin_token");
    const res = await fetch(`${API}/leads/export`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tailwind_leads_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Header */}
      <div className="bg-[#182649] py-6 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-back-link">
              <ArrowLeft className="h-5 w-5 text-white/60 hover:text-white transition-colors" />
            </a>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="admin-title">
              Lead Management
            </h1>
          </div>
          <div className="flex gap-3">
            <Button onClick={fetchData} variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full px-4 py-2 text-sm" data-testid="admin-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            <Button onClick={exportCSV} className="bg-[#D49A38] text-white hover:bg-[#c08a2e] rounded-full px-4 py-2 text-sm" data-testid="admin-export-btn">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button onClick={handleLogout} variant="outline" className="border-red-400/30 text-red-300 hover:bg-red-500/10 rounded-full px-4 py-2 text-sm" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          <div className="bg-white rounded-md border border-[#E2E8F0] p-5">
            <p className="text-sm text-[#4A5568]">Total Leads</p>
            <p className="text-2xl font-bold text-[#182649] mt-1" data-testid="stat-total">{stats.total}</p>
          </div>
          <div className="bg-white rounded-md border border-[#E2E8F0] p-5">
            <p className="text-sm text-[#4A5568]">New</p>
            <p className="text-2xl font-bold text-blue-600 mt-1" data-testid="stat-new">{stats.new}</p>
          </div>
          <div className="bg-white rounded-md border border-[#E2E8F0] p-5">
            <p className="text-sm text-[#4A5568]">Contacted</p>
            <p className="text-2xl font-bold text-yellow-600 mt-1" data-testid="stat-contacted">{stats.contacted}</p>
          </div>
          <div className="bg-white rounded-md border border-[#E2E8F0] p-5">
            <p className="text-sm text-[#4A5568]">Converted</p>
            <p className="text-2xl font-bold text-green-600 mt-1" data-testid="stat-converted">{stats.converted}</p>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-center text-[#4A5568] py-12">Loading leads...</p>
        ) : leads.length === 0 ? (
          <p className="text-center text-[#4A5568] py-12" data-testid="admin-empty">No leads yet.</p>
        ) : (
          <div className="bg-white rounded-md border border-[#E2E8F0] overflow-hidden" data-testid="admin-leads-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#182649] text-white text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Company</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Phone</th>
                    <th className="px-4 py-3 font-medium">Space</th>
                    <th className="px-4 py-3 font-medium">Activity</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-[#F8F9FA] transition-colors" data-testid={`lead-row-${lead.id}`}>
                      <td className="px-4 py-3 text-xs text-[#4A5568] whitespace-nowrap">{formatDate(lead.created_at)}</td>
                      <td className="px-4 py-3 font-medium text-[#182649]">{lead.name}</td>
                      <td className="px-4 py-3 text-[#4A5568]">{lead.company}</td>
                      <td className="px-4 py-3">
                        <a href={`mailto:${lead.email}`} className="text-[#3C75B6] hover:underline">{lead.email}</a>
                      </td>
                      <td className="px-4 py-3 text-[#4A5568] whitespace-nowrap">{lead.phone}</td>
                      <td className="px-4 py-3 text-[#4A5568] whitespace-nowrap">{lead.space_requirement}</td>
                      <td className="px-4 py-3 text-[#4A5568] max-w-[180px] truncate">{lead.business_activity}</td>
                      <td className="px-4 py-3">
                        <select
                          value={lead.status || "new"}
                          onChange={(e) => updateStatus(lead.id, e.target.value)}
                          className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${STATUS_COLORS[lead.status] || STATUS_COLORS.new}`}
                          data-testid={`status-select-${lead.id}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                          <option value="lost">Lost</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => deleteLead(lead.id)} className="text-red-400 hover:text-red-600 transition-colors" data-testid={`delete-${lead.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="mt-8 bg-white rounded-md border border-[#E2E8F0] p-6" data-testid="admin-info">
          <h3 className="text-sm font-bold text-[#182649] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Storage & Integration</h3>
          <ul className="text-sm text-[#4A5568] space-y-1">
            <li>Leads stored securely in MongoDB (production database)</li>
            <li>CSV export available via the button above or directly at <code className="text-xs bg-[#F8F9FA] px-1 py-0.5 rounded">/api/leads/export</code></li>
            <li>HubSpot integration ready — each lead has <code className="text-xs bg-[#F8F9FA] px-1 py-0.5 rounded">hubspot_synced</code> and <code className="text-xs bg-[#F8F9FA] px-1 py-0.5 rounded">hubspot_contact_id</code> fields</li>
            <li>Status tracking: New → Contacted → Converted / Lost</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
